<script>
  import { createEventDispatcher } from 'svelte';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';
  import { t, currentLang } from '../i18nStore';

  export let showModal = false;
  export let preSelectedCategories = [];
  export let locale = '';

  const dispatch = createEventDispatcher();

  let searchTerm = '';
  let searchResults = [];
  let selectedFromList = {};
  let allCategories = [];
  let isLoading = true;
  let error = '';

  $: effectiveLang = locale || $currentLang || 'en';

  function getCategoryDisplayName(cat) {
    if (!cat) return '';
    if (effectiveLang.startsWith('zh')) {
      return cat.nameZh || (cat.translations && (cat.translations['zh-HK'] || cat.translations['zh'])) || cat.name;
    } else if (effectiveLang.startsWith('ja')) {
      return cat.nameJa || (cat.translations && cat.translations['ja']) || cat.nameEn || cat.name;
    } else {
      return cat.nameEn || cat.name;
    }
  }

  async function initialize() {
    isLoading = true;
    error = '';
    selectedFromList = {};

    try {
      const response = await fetch(getApiUrl('/categories'));
      if (!response.ok) throw new Error($t('cat_modal.error_loading'));
      
      allCategories = await response.json();
      searchResults = allCategories;

      // Initialize selected states
      preSelectedCategories.forEach(catName => {
        selectedFromList[catName] = true;
      });

      // Also match canonical name if preSelected has legacy compound names
      allCategories.forEach(cat => {
        for (const p of preSelectedCategories) {
          if (p === cat.name || (cat.nameEn && p === cat.nameEn) || (cat.key && p.toLowerCase() === cat.key.toLowerCase())) {
            selectedFromList[cat.name] = true;
          }
        }
      });
      
    } catch (err) {
      error = err.message;
      console.error(err);
    } finally {
      isLoading = false;
    }
  }

  $: if (searchTerm) {
    const term = searchTerm.toLowerCase();
    searchResults = allCategories.filter(cat => {
      const name = (cat.name || '').toLowerCase();
      const nameEn = (cat.nameEn || '').toLowerCase();
      const nameZh = (cat.nameZh || '').toLowerCase();
      const nameJa = (cat.nameJa || '').toLowerCase();
      const key = (cat.key || '').toLowerCase();
      const display = getCategoryDisplayName(cat).toLowerCase();
      return name.includes(term) || nameEn.includes(term) || nameZh.includes(term) || nameJa.includes(term) || key.includes(term) || display.includes(term);
    });
  } else {
    searchResults = allCategories;
  }

  function closeModal() {
    dispatch('close');
  }

  function confirmSelection() {
    const finalSelection = Object.keys(selectedFromList).filter(key => selectedFromList[key]);
    dispatch('confirm', { selected: finalSelection });
  }

  $: if (showModal) {
    initialize();
  }
</script>

{#if showModal}
  <div class="modal-backdrop" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h3>{$t('cat_modal.title')} ({$selectedCountry})</h3>
        <button class="close-btn" on:click={closeModal}>&times;</button>
      </div>
      
      <div class="modal-body">
        <div class="search-section">
          <input type="text" bind:value={searchTerm} placeholder={$t('cat_modal.search_placeholder')} />
        </div>
        
        <div class="category-list">
          {#if isLoading}
            <p class="status-text">{$t('cat_modal.loading')}</p>
          {:else if error}
            <p class="status-text error">{error}</p>
          {:else if searchResults.length > 0}
            <div class="category-grid">
              {#each searchResults as category (category._id || category.name)}
                <label class="category-item" class:active={selectedFromList[category.name]}>
                  <input type="checkbox" bind:checked={selectedFromList[category.name]} />
                  <span class="category-name">
                    {getCategoryDisplayName(category)}
                    {#if category.nameEn && category.nameZh && effectiveLang.startsWith('zh') && category.nameEn !== category.nameZh}
                      <small class="category-subtext">({category.nameEn})</small>
                    {:else if category.nameZh && !effectiveLang.startsWith('zh')}
                      <small class="category-subtext">({category.nameZh})</small>
                    {/if}
                  </span>
                </label>
              {/each}
            </div>
          {:else}
            <p class="status-text">{$t('cat_modal.no_results')}</p>
          {/if}
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn-secondary" on:click={closeModal}>{$t('common.cancel')}</button>
        <button class="btn-primary" on:click={confirmSelection}>{$t('cat_modal.confirm_btn')}</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed; top: 0; left: 0; width: 100%; height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex; justify-content: center; align-items: center; z-index: 1000;
  }
  .modal-content {
    background: white; border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0,0,0,0.3);
    width: 90%; max-width: 500px; max-height: 85vh;
    display: flex; flex-direction: column; overflow: hidden;
  }
  .modal-header {
    padding: 1rem 1.5rem; border-bottom: 1px solid #eee;
    display: flex; justify-content: space-between; align-items: center;
  }
  .modal-header h3 { margin: 0; font-size: 1.2rem; }
  .close-btn { background: none; border: none; font-size: 1.8rem; cursor: pointer; color: #999; }

  .modal-body { padding: 1.5rem; overflow-y: auto; flex-grow: 1; }
  
  .search-section input {
    width: 100%; padding: 0.7rem; border: 1px solid #ddd; border-radius: 6px;
    box-sizing: border-box; margin-bottom: 1rem; font-size: 1rem;
  }

  .category-list {
    min-height: 150px;
  }

  .category-grid {
    display: flex; flex-direction: column; gap: 0.25rem;
  }

  .category-item {
    display: flex; align-items: center; gap: 10px; padding: 0.75rem;
    border-bottom: 1px solid #f9f9f9; cursor: pointer; border-radius: 6px;
    transition: background 0.2s;
  }
  .category-item:hover { background-color: #f8f9fa; }
  .category-item input { width: 1.2rem; height: 1.2rem; }

  .status-text { text-align: center; color: #666; padding: 2rem 0; }
  .status-text.error { color: #dc3545; }

  .modal-footer {
    padding: 1rem 1.5rem; border-top: 1px solid #eee;
    display: flex; justify-content: flex-end; gap: 1rem; background: #fff;
  }
  .btn-primary, .btn-secondary {
    padding: 0.6rem 1.2rem; border-radius: 6px;
    border: none; cursor: pointer; font-size: 1rem; font-weight: 500;
  }
  .btn-primary { background-color: #007bff; color: white; }
  .btn-secondary { background-color: #f0f0f0; color: #333; }

  /* Mobile Responsive - Toggle Buttons */
  @media (max-width: 600px) {
    .category-grid {
      display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;
    }

    .category-item {
      justify-content: center; text-align: center; border: 1px solid #ddd;
      background-color: #fff; padding: 1rem 0.5rem;
    }
    
    .category-item input { display: none; } /* Hide checkbox */
    
    .category-item.active {
      background-color: #007bff; border-color: #007bff; color: white;
    }

    .category-name { font-size: 0.9rem; font-weight: 500; }
  }

  .category-subtext {
    font-size: 0.8rem;
    color: #888;
    margin-left: 4px;
  }
</style>